import { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Printer, ClipboardList, Warehouse, FileDown, AlertTriangle, Clock } from "lucide-react";
import { api, formatApiError, downloadFile } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { PrintDialog } from "@/components/PrintDialog";
import { cn } from "@/lib/utils";

const PLATFORMS = [
  { id: "amazon", name: "Amazon", color: "#F59E0B" },
  { id: "flipkart", name: "Flipkart", color: "#60A5FA" },
  { id: "firstcry", name: "Firstcry", color: "#F472B6" },
  { id: "myntra", name: "Myntra", color: "#E879F9" },
];

export default function PlatformPage() {
  const [params, setParams] = useSearchParams();
  const platform = params.get("platform") || "amazon";
  const meta = PLATFORMS.find((p) => p.id === platform) || PLATFORMS[0];
  const [warehouses, setWarehouses] = useState([]);
  const [stats, setStats] = useState(null);
  const [printIds, setPrintIds] = useState(null);
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    const [w, s] = await Promise.all([api.get("/warehouses", { params: { marketplace: platform } }), api.get("/stats")]);
    setWarehouses(w.data);
    setStats(s.data);
  }, [platform]);
  useEffect(() => { load(); }, [load]);

  const pending = warehouses.reduce((a, w) => a + w.pending, 0);
  const unmatched = warehouses.find((w) => w.warehouse_id === "UNMATCHED")?.pending ?? 0;
  const list = warehouses.filter((w) => w.warehouse_id !== "UNMATCHED");

  const print = async (extra, key, emptyMsg) => {
    setBusy(key);
    try {
      const { data } = await api.get("/labels/ids", { params: { status: "pending", marketplace: platform, ...extra } });
      if (!data.ids.length) return toast.info(emptyMsg);
      setPrintIds(data.ids);
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(null); }
  };
  const pdf = (wh) => downloadFile(`/picklist/pdf?status=pending&marketplace=${platform}${wh ? `&warehouse_id=${encodeURIComponent(wh)}` : ""}`, `picklist-${platform}${wh ? "-" + wh : ""}.pdf`).catch((e) => toast.error(formatApiError(e)));

  return (
    <div className="space-y-8" data-testid="platform-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="font-mono text-[11px] uppercase tracking-widest text-emerald-400">Print by marketplace</div>
          <h1 className="font-heading text-3xl font-extrabold uppercase tracking-tight sm:text-4xl lg:text-5xl">Platform</h1>
        </div>
        <div className="flex gap-1 rounded-sm border border-slate-800 bg-slate-950 p-1" data-testid="platform-switch">
          {PLATFORMS.map((p) => (
            <button key={p.id} type="button" data-testid={`platform-tab-${p.id}`} onClick={() => setParams({ platform: p.id })}
              className={cn("flex items-center gap-2 rounded-sm px-4 py-2 text-xs font-semibold uppercase tracking-widest transition-colors duration-200", platform === p.id ? "text-slate-950" : "text-slate-400 hover:text-slate-100")}
              style={platform === p.id ? { backgroundColor: p.color } : undefined}>
              {p.name}<span className={cn("rounded-sm px-1.5 py-0.5 font-mono text-[10px]", platform === p.id ? "bg-black/20" : "bg-slate-800")}>{stats?.pending_by_marketplace?.[p.id] ?? 0}</span>
            </button>
          ))}
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-[1.2fr_1fr]">
        <div className={cn("flex flex-col rounded-md border bg-[#111827] p-5", pending > 0 ? "border-amber-700/60" : "border-emerald-700/50")} data-testid="platform-pending-box">
          <div className="flex items-start justify-between">
            <div>
              <div className="font-mono text-[11px] uppercase tracking-widest" style={{ color: meta.color }}>{meta.name} · pending labels</div>
              <div className="font-heading text-5xl font-extrabold tracking-tight text-amber-300 sm:text-6xl" data-testid="platform-pending-count">{pending}</div>
              <div className="mt-1 font-mono text-[11px] uppercase tracking-widest text-slate-400">{pending - unmatched} ready · {unmatched} unmatched · {list.length} warehouse{list.length === 1 ? "" : "s"}</div>
            </div>
            <Clock className="h-6 w-6 text-amber-400/70" />
          </div>
          <div className="mt-5 flex flex-wrap gap-2">
            <Button data-testid="platform-print-all" className="bg-blue-600 hover:bg-blue-700" disabled={!pending || busy === "all"} onClick={() => print({}, "all", `No pending ${meta.name} labels`)}><Printer className="mr-2 h-4 w-4" /> Print all {pending} pending</Button>
            <Button data-testid="platform-print-unmatched" variant="outline" className="border-red-700/70 text-red-200 hover:bg-red-900/40" disabled={!unmatched || busy === "un"} onClick={() => print({ warehouse_id: "unmatched" }, "un", "No unmatched pending labels")}><AlertTriangle className="mr-2 h-4 w-4" /> Print {unmatched} unmatched</Button>
            <Button data-testid="platform-picklist-pdf" variant="outline" className="border-slate-700 bg-transparent hover:bg-slate-800" disabled={!pending} onClick={() => pdf()}><FileDown className="mr-2 h-4 w-4" /> Picklist PDF</Button>
            <Button asChild variant="outline" className="border-slate-700 bg-transparent hover:bg-slate-800"><Link to={`/labels?status=pending&marketplace=${platform}&date=all`} data-testid="platform-view-labels">View labels</Link></Button>
          </div>
        </div>
        <div className="rounded-md border border-slate-800 bg-[#111827] p-5 font-mono text-xs" data-testid="platform-warehouse-summary">
          <div className="mb-2 text-[11px] uppercase tracking-widest text-slate-400">Pending by warehouse</div>
          {list.filter((w) => w.pending > 0).length === 0 && <div className="text-slate-500">Nothing pending.</div>}
          {list.filter((w) => w.pending > 0).map((w) => (
            <div key={w.warehouse_id} className="flex justify-between border-b border-slate-800/60 py-1"><span className="text-blue-300">{w.warehouse_id}</span><span className="text-slate-200">{w.pending} labels · {w.pending_qty} units</span></div>
          ))}
        </div>
      </div>

      <div className="space-y-3" data-testid="platform-warehouse-grid">
        <h2 className="font-heading text-lg font-bold uppercase tracking-tight sm:text-xl">{meta.name} · print by warehouse</h2>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {list.map((w) => (
            <div key={w.warehouse_id} data-testid={`platform-warehouse-card-${w.warehouse_id}`} className="stat-card flex h-full flex-col justify-between rounded-md border border-slate-800 bg-[#111827] p-4 hover:border-blue-500/50">
              <div>
                <div className="flex items-center gap-2 font-mono text-sm font-semibold text-blue-300"><Warehouse className="h-4 w-4" />{w.warehouse_id}</div>
                <div className="mt-2 flex gap-3 font-mono text-xs text-slate-400"><span className="text-amber-300">{w.pending} pending</span><span className="text-emerald-400">{w.printed} printed</span></div>
              </div>
              <div className="mt-3 flex gap-2">
                <Button data-testid={`platform-warehouse-print-${w.warehouse_id}`} size="sm" className="flex-1 bg-blue-600 hover:bg-blue-700" disabled={!w.pending || busy === w.warehouse_id} onClick={() => print({ warehouse_id: w.warehouse_id }, w.warehouse_id, "No pending labels for this warehouse")}><Printer className="mr-1 h-3.5 w-3.5" /> Print {w.pending}</Button>
                <Button asChild size="sm" variant="outline" className="border-slate-700 bg-transparent hover:bg-slate-800"><Link to={`/picklist?warehouse=${encodeURIComponent(w.warehouse_id)}&marketplace=${platform}&date=all`} title="Picklist" data-testid={`platform-warehouse-picklist-${w.warehouse_id}`}><ClipboardList className="h-3.5 w-3.5" /></Link></Button>
                <Button size="sm" variant="outline" className="border-slate-700 bg-transparent hover:bg-slate-800" title="Picklist PDF" data-testid={`platform-warehouse-pdf-${w.warehouse_id}`} onClick={() => pdf(w.warehouse_id)}><FileDown className="h-3.5 w-3.5" /></Button>
              </div>
            </div>
          ))}
          {list.length === 0 && <div className="text-sm text-slate-500">No {meta.name} labels uploaded yet.</div>}
        </div>
      </div>

      <PrintDialog labelIds={printIds} open={!!printIds} onOpenChange={(o) => !o && setPrintIds(null)} onCompleted={load} onReplaceIds={setPrintIds} />
    </div>
  );
}
